/**
 * The way out of the room: an idea becomes a subject.
 *
 * The brief becomes the subject's description and the terms the room pulled
 * numbers for become its keywords, so the next thing you do is build pages
 * against them rather than re-type them.
 *
 * TWO WAYS TO START, because starting the crawl costs money: one creates the
 * subject and starts research, the other creates it paused. The result says
 * which way it went.
 *
 * `canPublish` comes from the page. A subject is Publish's, and someone who does
 * not hold it is told so rather than shown buttons that answer 402.
 */
import { useState } from 'react';
import api from '../../services/api';
import ShellLink from '../common/ShellLink';
import type { BoardroomPromoteResult } from '../../types';

export default function PromoteIdea({
  boardroomId,
  ideaId,
  promotedProjectId = null,
  canPublish,
  compact = false,
}: {
  boardroomId: number;
  ideaId: number;
  /** Set when the idea already became a subject. */
  promotedProjectId?: number | null;
  canPublish: boolean;
  compact?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BoardroomPromoteResult | null>(null);

  const projectId = result?.project_id ?? promotedProjectId;

  if (projectId) {
    return (
      <div className="text-xs text-emerald-800 dark:text-emerald-300" data-testid={`promoted-${ideaId}`}>
        {result
          ? result.note ??
            (result.builds_pages
              ? `Content plan created with ${result.keywords_added} keyword${result.keywords_added === 1 ? '' : 's'}.`
              : `Subject created with ${result.keywords_added} keyword${result.keywords_added === 1 ? '' : 's'}. It is a site ABOUT the idea - the thing itself is not something built here.`)
          : 'This idea is a subject.'}{' '}
        {result && !result.is_active && (
          <span className="text-amber-700 dark:text-amber-400">
            It is paused, so nothing is crawled or built until you turn it on.{' '}
          </span>
        )}
        <ShellLink to={`/subjects/${projectId}`} className="font-semibold underline">
          Open it
        </ShellLink>
      </div>
    );
  }

  if (!canPublish) {
    return compact ? null : (
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Making this idea a subject to build pages for needs Publish.
      </p>
    );
  }

  const go = async (activate: boolean) => {
    setBusy(true);
    setError(null);
    try {
      setResult(await api.promoteBoardroomIdea(boardroomId, ideaId, activate));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not make the subject');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={`promote-${ideaId}`}>
      <button
        type="button"
        disabled={busy}
        onClick={() => go(true)}
        className="px-2 py-1 rounded bg-gray-900 text-white hover:bg-black disabled:opacity-40 dark:bg-gray-100 dark:text-gray-900"
        title="Creates a subject from this idea and starts researching its keywords"
      >
        Make this a subject
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => go(false)}
        className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40"
        title="Creates the subject without spending anything yet"
      >
        Create it paused
      </button>
      {error && (
        <span className="text-red-600 dark:text-red-400" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
